<script setup lang="ts">
import type { SelectProps } from '~/types/input';

const {
    showLabel = true,
    validationType = 'none',
    successMessage = '',
    infoMessage = '',
    errorMessage = '',
    warningMessage = '',
    required = false,
    showOptionalLabel = true,
} = defineProps<SelectProps>();

const model = defineModel<string>();

const select = useTemplateRef<HTMLSelectElement>('select');

const validationMessage = computed(() => {
    return {
        error: errorMessage,
        success: successMessage ?? '',
        warning: warningMessage ?? '',
        info: infoMessage ?? '',
        none: '',
    };
});

watch(
    () => validationType,
    () => {
        if (validationType === 'none') {
            select.value?.setCustomValidity('');
            return;
        }

        select.value?.setCustomValidity(errorMessage);
    }
);
</script>

<template>
    <div class="flex flex-col">
        <label v-if="showLabel" :for="name" class="flex justify-between font-semibold">
            {{ label }}
            <span v-if="!required && showOptionalLabel" class="font-normal">{{
                $t('form.optional')
            }}</span>
        </label>
        <div class="relative w-full">
            <select
                :id="name"
                ref="select"
                v-model="model"
                :name="name"
                class="input-field"
                :required="required">
                <option value="" disabled>{{ $t('form.selectValue') }}</option>
                <option v-for="(value, key) in data" :key="key" :value="key">
                    {{ value }}
                </option>
            </select>
            <SvgComponent
                width="16"
                height="16"
                class="absolute top-1/2 right-2 -translate-y-1/2 transform"
                icon="chevron-down" />
        </div>
        <HelperText
            v-if="validationType !== 'none' && validationType !== undefined"
            class="mt-2"
            :icon="validationType"
            :variant="validationType"
            :text="validationMessage[validationType]" />
    </div>
</template>

<style lang="css">
@reference "~/assets/css/main.css";

.input-field {
    @apply text-brand-black border-brand-black-60 h-auto w-full appearance-none rounded border py-[13px] pr-4 pl-2 text-[15px] leading-4.5 focus:border-black focus:text-black;
}
</style>
